import { ArrowForward, Block, WarningAmber } from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import type { MoveRequest } from "../hooks/useCalendarDrag";
import { formatDuration, formatWindowParts } from "../utils/calendarUtils";
import {
	ConfirmEventChangeModal,
	EventChangeNotice,
} from "./ConfirmEventChangeModal";
import { type CalendarItem, itemColors } from "./EventBar";
import { ItemMovingCard, ItemSummaryCard } from "./EventSummaryCard";

// One step of the landing sequence: label, card (or an empty placeholder) and
// the window under it
const Slot = ({
	label,
	item,
	start,
	end,
	emptyText,
	here = false,
}: {
	label: string;
	item: CalendarItem | null;
	start?: Date;
	end?: Date;
	emptyText?: string;
	/** the moved item at its new position */
	here?: boolean;
}) => {
	const window = start && end ? formatWindowParts(start, end) : null;
	return (
		<Box
			sx={{
				flex: "1 1 0",
				minWidth: 0,
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography
				sx={{
					fontSize: "0.6875rem",
					fontWeight: 700,
					letterSpacing: "0.08em",
					textTransform: "uppercase",
					lineHeight: "16px",
					mb: 1,
					color: here && item ? itemColors(item).color : "text.secondary",
				}}
			>
				{label}
			</Typography>
			{item ? (
				<ItemSummaryCard item={item} dropSlot={here} />
			) : (
				<Box
					sx={{
						flex: 1,
						minHeight: 64,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: "1px dashed #cfd3d9",
						borderRadius: "4px",
						color: "text.disabled",
						fontSize: "0.75rem",
					}}
				>
					{emptyText}
				</Box>
			)}
			<Typography
				noWrap
				sx={{
					mt: 1,
					fontSize: "0.75rem",
					lineHeight: "18px",
					color: "text.secondary",
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{window ? (
					<>
						{window.day}{" "}
						<Box component="b" sx={{ fontWeight: 500, color: "text.primary" }}>
							{window.hours}
						</Box>
					</>
				) : (
					" "
				)}
			</Typography>
		</Box>
	);
};

const SlotArrow = () => (
	<Box sx={{ display: "flex", alignItems: "center", color: "text.disabled" }}>
		<ArrowForward fontSize="small" />
	</Box>
);

type Props = {
	loading?: boolean;
	moveData: MoveRequest;
	onConfirm: () => void;
	onCancel: () => void;
};

export const ConfirmMoveModal = ({
	loading = false,
	moveData,
	onConfirm,
	onCancel,
}: Props) => {
	const {
		item,
		newStart,
		newEnd,
		pushCount,
		blocked,
		neighborBefore,
		neighborAfter,
	} = moveData;
	return (
		<ConfirmEventChangeModal
			title={
				item.kind === "wo" ? "Confirm Move Work Order" : "Confirm Move Event"
			}
			actionLabel="Moving"
			confirmLabel="Confirm Move"
			summary={
				<ItemMovingCard
					item={item}
					duration={formatDuration(newStart, newEnd)}
				/>
			}
			oldStart={item.start}
			oldEnd={item.end}
			newStart={newStart}
			newEnd={newEnd}
			loading={loading}
			disabled={blocked}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			{blocked && (
				<EventChangeNotice tone="error">
					<Block fontSize="small" />
					Overlaps a fixed work order window, maintenance, downtime or holiday —
					this move is not allowed
				</EventChangeNotice>
			)}
			{!blocked && pushCount > 0 && (
				<EventChangeNotice tone="warning">
					<WarningAmber fontSize="small" />
					{pushCount} planned work order{pushCount > 1 ? "s" : ""} in the way
					will be pushed later
				</EventChangeNotice>
			)}
			{(neighborBefore || neighborAfter) && (
				<>
					<Divider sx={{ mt: 0.5, mb: 2.5 }} />
					<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
						<Typography variant="subtitle2" fontWeight={700}>
							Where it lands
						</Typography>
						<Typography variant="caption" color="text.secondary">
							in time order
						</Typography>
					</Box>
					<Box sx={{ display: "flex", alignItems: "stretch", gap: 1.5 }}>
						<Slot
							label="Previous"
							item={neighborBefore}
							start={neighborBefore?.start}
							end={neighborBefore?.end}
							emptyText="Nothing planned before"
						/>
						<SlotArrow />
						<Slot
							label="Moving here"
							item={item}
							start={newStart}
							end={newEnd}
							here
						/>
						<SlotArrow />
						<Slot
							label="Next"
							item={neighborAfter}
							start={neighborAfter?.start}
							end={neighborAfter?.end}
							emptyText="Nothing planned after"
						/>
					</Box>
				</>
			)}
		</ConfirmEventChangeModal>
	);
};
